import { motion, AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';
import { usePostMessage, useQueryData, useSubscribeData } from 'spectacle-ui-game-bridge-react';
import { PreloginContent } from '../../hooks/useContentForBuild';
import { usePreloginContext } from '../../context/PreloginContext';
import Modal from '../../components/modal/modal';
import useDlogLoginFunnel from '../../hooks/useDlogLoginFunnel';
import usePreloadImages from '../../hooks/usePreloadImages';
import './notificationsPrompt.sass';

type NotificationsPromptComponent = {
  content: PreloginContent | null;
};

export default function NotificationsPrompt({ content }: NotificationsPromptComponent) {
  const dlog = useDlogLoginFunnel();
  const { isSoftPromptShown, setIsSoftPromptShown } = usePreloginContext();
  const [notificationSoftpromptTimestamp] = useQueryData<number>('webviewdata', 'notificationSoftpromptTimestamp');
  const notificationsEnabled = useSubscribeData<boolean>('webviewdata', 'notificationsEnabled');
  const notificationSoftpromptShown = usePostMessage('notificationSoftpromptShown');
  const displayNotificationPrompt = usePostMessage('displayNotificationPrompt');
  const bg = 'https://prod.cdni.callofduty.com/prelogin/media/img/permission_prompt_bg.jpg';
  const deco = 'https://prod.cdni.callofduty.com/prelogin/media/img/permission_prompt_ghost.png';
  const imagesLoaded = usePreloadImages([bg, deco]);

  const acceptSoftPrompt = () => {
    dlog({
      phase: 'Notification_Request_Prompt',
      action: 'Click',
      targets: 'Allow',
    });
    displayNotificationPrompt();
    setIsSoftPromptShown(false);
  };

  const cancelSoftPrompt = () => {
    dlog({
      phase: 'Notification_Request_Prompt',
      action: 'Click',
      targets: 'Not_Now',
    });
    setIsSoftPromptShown(false);
  };

  useEffect(() => {
    const timestamp = notificationSoftpromptTimestamp ?? 0;

    if (content && notificationsEnabled !== null) {
      const dateCheck = Date.now() / 1000 - timestamp;

      if ((!notificationsEnabled && dateCheck > Number(content.softPromptInterval)) || (!notificationsEnabled && !timestamp)) {
        setIsSoftPromptShown(true);
        notificationSoftpromptShown();
        dlog({
          phase: 'Notification_Request_Prompt',
          action: 'Render',
        });
      }
    }
  }, [content, notificationSoftpromptTimestamp, setIsSoftPromptShown, notificationsEnabled, notificationSoftpromptShown, dlog]);

  return (
    <AnimatePresence>
      {isSoftPromptShown && imagesLoaded && (
        <motion.div key="softPrompt" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="signin">
          <Modal additionalClass="frame--nopadding" hasExit={false} hideCorners={true}>
            <div className="prompt">
              <div className="prompt__content">
                <div className="prompt__inner-content">
                  <img alt="" src={deco} className="prompt__deco" />
                  <h1>{content?.softPromptHeading}</h1>
                  <p>{content?.softPromptDesc}</p>
                  <div className="prompt__ctas">
                    <button className="cta cta--muted cta--wide" onClick={cancelSoftPrompt}>
                      {content?.softPromptCancel}
                    </button>
                    <button className="cta cta--wide" onClick={acceptSoftPrompt}>
                      {content?.softPromptAccept}
                    </button>
                  </div>
                </div>
              </div>
              <img alt="" src={bg} className="prompt__bg" />
            </div>
          </Modal>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
